import React, { useState, useCallback } from 'react';
import MapView from './MapView';
import LocationAutocomplete from './LocationAutocomplete';
import LocationList from './LocationList';
import { useLeafletMap } from './useLeafletMap';
import { reverseGeocode, getBahrainCenter } from './locationUtils';

/**
 * Main location picker component
 * Combines autocomplete search, map selection, and saved locations list
 *
 * @param {Object} props
 * @param {string} props.mode - 'single' or 'multiple'
 * @param {Object} props.initialLocation - Initial map center { lat, lng }
 * @param {Array} props.selectedLocations - Currently selected locations
 * @param {Function} props.onLocationSelect - Callback when location is selected
 * @param {Function} props.onLocationRemove - Callback to remove location (multiple mode)
 * @param {Function} props.onSetDefault - Callback to set default location (multiple mode)
 * @param {Array} props.teacherLocations - Teacher's locations to display (student mode)
 * @param {boolean} props.showAutocomplete - Show search input
 * @param {boolean} props.showClickToSelect - Allow map clicking
 * @param {boolean} props.readOnly - Read-only mode
 * @param {string} props.height - Map height
 * @param {number} props.zoom - Initial zoom level
 * @param {boolean} props.isArabic - RTL support
 */
const LocationPicker = ({
  mode = 'single',
  initialLocation,
  selectedLocations = [],
  onLocationSelect,
  onLocationRemove,
  onSetDefault,
  teacherLocations = [],
  showAutocomplete = true,
  showClickToSelect = true,
  readOnly = false,
  height = '400px',
  zoom = 12,
  isArabic = false
}) => {
  const defaultCenter = initialLocation || getBahrainCenter();
  const [selectedPosition, setSelectedPosition] = useState(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : null
  );
  const [isGeocoding, setIsGeocoding] = useState(false);

  const { isLoaded, loadError } = useLeafletMap();

  const handleMapClick = useCallback(async (position) => {
    if (readOnly || !showClickToSelect) return;

    setSelectedPosition(position);
    setIsGeocoding(true);

    try {
      // Reverse geocode to get address
      const address = await reverseGeocode(position);

      onLocationSelect({
        latitude: position.lat,
        longitude: position.lng,
        formatted_address: address,
        place_name: 'Custom Location',
        place_id: null
      });
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      // Still allow selection even if geocoding fails
      onLocationSelect({
        latitude: position.lat,
        longitude: position.lng,
        formatted_address: `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`,
        place_name: 'Custom Location',
        place_id: null
      });
    } finally {
      setIsGeocoding(false);
    }
  }, [readOnly, showClickToSelect, onLocationSelect]);

  const handleAutocompleteSelect = useCallback((place) => {
    const location = {
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      formatted_address: place.formatted_address,
      place_id: place.place_id,
      place_name: place.name
    };

    setSelectedPosition({
      lat: location.latitude,
      lng: location.longitude
    });

    onLocationSelect(location);
  }, [onLocationSelect]);

  if (loadError) {
    return (
      <div style={{
        padding: 20,
        background: '#fef2f2',
        border: '2px solid #fecaca',
        borderRadius: 12,
        color: '#991b1b',
        textAlign: 'center',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'خطأ في تحميل الخرائط' : 'Error loading maps'}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{
        padding: 20,
        background: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: 12,
        color: '#64748b',
        textAlign: 'center',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        {isArabic ? 'جاري تحميل الخرائط...' : 'Loading maps...'}
      </div>
    );
  }

  // Prepare markers for display
  const mapMarkers = [
    // Selected location marker (red)
    ...(selectedPosition ? [{
      position: selectedPosition,
      type: 'selected',
      label: isArabic ? 'الموقع المحدد' : 'Selected Location'
    }] : []),
    // Teacher locations markers (blue)
    ...teacherLocations.map(loc => ({
      position: { lat: loc.latitude, lng: loc.longitude },
      type: 'teacher',
      label: loc.place_name || loc.formatted_address
    }))
  ];

  return (
    <div style={{ width: '100%' }}>
      {showAutocomplete && (
        <LocationAutocomplete
          onPlaceSelect={handleAutocompleteSelect}
          isArabic={isArabic}
        />
      )}

      {isGeocoding && (
        <div style={{
          marginBottom: 12,
          padding: '8px 12px',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 8,
          fontSize: 12,
          color: '#0369a1',
          textAlign: isArabic ? 'right' : 'left',
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}>
          {isArabic ? 'جاري تحديد العنوان...' : 'Getting address...'}
        </div>
      )}

      <MapView
        center={selectedPosition || defaultCenter}
        zoom={zoom}
        height={height}
        onMapClick={handleMapClick}
        markers={mapMarkers}
        readOnly={readOnly}
      />

      {!readOnly && showClickToSelect && (
        <div style={{
          marginTop: 12,
          padding: '8px 12px',
          background: '#f8fafc',
          borderRadius: 8,
          fontSize: 12,
          color: '#64748b',
          textAlign: isArabic ? 'right' : 'left',
          fontFamily: "'IBM Plex Sans Arabic', sans-serif"
        }}>
          💡 {isArabic
            ? 'انقر على الخريطة لتحديد موقع أو استخدم البحث أعلاه'
            : 'Click on the map to select a location or use the search above'}
        </div>
      )}

      {mode === 'multiple' && (
        <LocationList
          locations={selectedLocations}
          onLocationRemove={onLocationRemove}
          onSetDefault={onSetDefault}
          isArabic={isArabic}
        />
      )}
    </div>
  );
};

export default LocationPicker;
